































































































import { Component } from 'vue-property-decorator';
import Vue from 'vue';
import PsbMultiplierComponent from '@/earn/components/psb/psb-multiplier.component.vue';
import PsbMatchComponent from '@/earn/components/psb/psb-match.component.vue';
import PsbComponent from '@/earn/components/psb/psb.component.vue';
import BreadcrumbInterface from '@/_shared/components/layout/breadcrumb.interface';
import Breadcrumb from '@/_shared/components/layout/breadcrumb.component.vue';
import Disclaimer from '@/_shared/components/globals/disclaimer.component.vue';
import Rep from '@/_shared/entities/rep/rep.entity';
import PsbService from '@/earn/services/psb.service';
import PsbMultiplier from '@/earn/entities/psb/psb-multiplier.entity';
import PsbMatch from '@/earn/entities/psb/psb-match.entity';
import PsbResult from '@/earn/entities/psb/psb-result.entity';
import DateService from '@/_shared/services/date.service';
import authService from '@/_shared/services/auth/auth.service';

@Component({
  components: {
    Breadcrumb,
    PsbMultiplierComponent,
    PsbMatchComponent,
    PsbComponent,
    Disclaimer,
  },
})
export default class ProductShareBonusPage extends Vue {
  private breadcrumb: BreadcrumbInterface[] = [];
  private hasFinishedLoading = false;

  private tabIndex = 0;
  private tabs = ['multiplier', 'match', 'psb'];

  private repInfo: Rep | null = null;
  private repDIDParam: number | null = null;

  private psbMultiplier: PsbMultiplier | null = null;
  private psbMatch: PsbMatch | null = null;
  private psbResult: PsbResult | null = null;

  async created() {
    this.$store.dispatch('showLoading');
    this.breadcrumb = this.$route.meta.breadcrumb;

    this.repDIDParam = this.retrieveDidFromParam();
    if (!this.repDIDParam || this.repDIDParam === +this.rep.did) {
      await this.loadUserData();
      await this.loadMatchingEnrollees(this.rep.did);
    } else {
      await this.loadRepInfo(String(this.repDIDParam));
      await this.loadDownLineData(this.repDIDParam);
      await this.loadMatchingEnrollees(this.repDIDParam);
    }

    if (!this.hasEnoughEnrollees) {
      this.removeTab('match');
    }

    if (this.psbMultiplier?.isPastTargetDate() && this.psbMatch?.isPastTargetDate()) {
      this.removeTab('multiplier');
    }

    this.goToFirstTab();
    this.finishLoading();
  }

  get rep(): Rep {
    return this.$store.state.RepStore.rep;
  }

  get repName(): string {
    if (this.repInfo) return this.repInfo.recognitionName;
    return this.rep.recognitionName;
  }

  get shouldShowOtherBonus(): boolean {
    return this.tabs.length > 1;
  }

  get multiplierTitle() {
    if (!this.$t) return '';
    return this.$t('PRODUCT_SHARING_BONUS.MULTIPLIER_TITLE');
  }

  get matchTitle() {
    if (!this.$t) return '';
    return this.$t('PRODUCT_SHARING_BONUS.MATCH_TITLE');
  }

  get psbTitle() {
    if (!this.$t) return '';
    return this.$t('PRODUCT_SHARING_BONUS.PSB_TITLE');
  }

  get hasMultiplierBonus(): boolean {
    if (!this.psbMultiplier) return false;
    const multiplierIndex = this.tabs.indexOf('multiplier');
    return multiplierIndex > -1;
  }

  get hasMatchingBonus(): boolean {
    if (!this.psbMatch) return false;
    const matchIndex = this.tabs.indexOf('match');
    return matchIndex > -1;
  }

  get hasPsbResults(): boolean {
    return this.psbResult !== null;
  }

  get hasEnoughEnrollees(): boolean {
    if (!this.psbMultiplier) return false;
    return this.psbMultiplier?.hasEnoughEnrollees();
  }

  get hasAtLeastOneSubEnrollee(): boolean {
    if (!this.psbMatch) return false;
    return this.psbMatch?.totalEnrolleesWithSubEnrollees > 0;
  }

  get hasAtLeastOneCompletedSubEnrollee(): boolean {
    if (!this.psbMatch) return false;
    return this.psbMatch?.totalEnrolleesWithCompleteSubEnrollees > 0;
  }

  get activeTab() {
    const tabName = this.tabs[this.tabIndex];
    switch (tabName) {
      case 'multiplier': return this.$t('PRODUCT_SHARING_BONUS.MULTIPLIER_TITLE');
      case 'match': return this.$t('PRODUCT_SHARING_BONUS.MATCH_TITLE');
      case 'psb': return this.$t('PRODUCT_SHARING_BONUS.PSB_TITLE');
      default: return '';
    }
  }

  get showNoResultsMessage(): boolean {
    const isMissingMinimumData = (this.psbResult === null && this.psbMultiplier === null);
    return (this.hasFinishedLoading && isMissingMinimumData);
  }

  get matchTargetDate(): Date {
    if (!this.psbMatch) return new Date();
    return this.psbMatch.targetDate;
  }

  get showDisclaimer(): boolean {
    const tabName = this.tabs[this.tabIndex];
    switch (tabName) {
      case 'multiplier': return this.hasMultiplierBonus;
      case 'match': return this.hasAtLeastOneCompletedSubEnrollee;
      case 'psb': return this.checkDisclaimerForPsb();
      default: return false;
    }
  }

  private checkDisclaimerForPsb(): boolean {
    return (this.psbResult?.enrollees.length || 0) > 0;
  }

  public changeTab(tabName: any): void {
    this.tabIndex = this.tabs.indexOf(tabName);
  }

  public async changePsbMonth(date: Date) {
    this.$store.dispatch('showLoading');
    await this.loadResultsForMonth(date);
    this.finishLoading();
  }

  public async loadResultsForMonth(date: Date) {
    await PsbService.getPsbResults(this.rep.did, date)
      .then((response) => {
        this.psbResult = response;
      })
      .catch(() => {
        this.finishLoading();
      });
  }

  public async loadMultiplierEnrollments(repDid: number) {
    await PsbService.getMultiplierEnrollments(repDid)
      .then((response) => {
        this.psbMultiplier = response;
      })
      .catch(() => {
        this.removeTab('multiplier');
        this.finishLoading();
      });
  }

  public async loadMatchingEnrollees(repDid: number) {
    await PsbService.getMatchEnrollees(repDid)
      .then((response) => {
        this.psbMatch = response;
        if (this.psbMatch.isPastTargetDate()) {
          this.removeTab('match');
        }
      })
      .catch(() => {
        this.removeTab('match');
        this.finishLoading();
      });
  }

  private loadRepInfo(repDid: string): void {
    authService.getRepInfo(repDid)
      .then((repInfo) => {
        this.repInfo = repInfo;
      });
  }

  private retrieveDidFromParam(): number {
    const encodedDidParam = this.$route.params.repDID;
    const hashId = authService.getHashIds();
    const decodedDid = hashId.decode(encodedDidParam)[0];
    return Number(decodedDid);
  }

  private async loadDownLineData(repDid: number) {
    this.removeTab('match');
    this.removeTab('psb');
    await this.loadMultiplierEnrollments(repDid);

    if (this.psbMultiplier === null || this.psbMultiplier.isPastTargetDate()) {
      this.$router.push({ name: 'not-found' });
    }
  }

  private async loadUserData() {
    const currentDate = DateService.getCurrentDateInUtc();
    await this.loadResultsForMonth(currentDate);

    await this.loadMultiplierEnrollments(this.rep.did);
    if (this.psbMultiplier?.isPastTargetDate()) {
      this.removeTab('multiplier');
    }
  }

  private goToFirstTab(): void {
    const firstTab = this.tabs[0];
    this.changeTab(firstTab);
  }

  private removeTab(tabName: string): void {
    const index = this.tabs.indexOf(tabName);
    if (index >= 0) {
      this.tabs.splice(index, 1);
    }
  }

  finishLoading() {
    this.hasFinishedLoading = true;
    this.$store.dispatch('hideLoading');
  }
}
